"use client";

import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { useState, useEffect } from "react";
import { useRouter } from "nextjs-toploader/app";
import { noNumberCopy, numbersSettingsPath } from "@/app/_module/components/NoActiveNumberNotice";
import { AlertTriangle, CreditCard, Settings, X, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { routes } from "@/app/_module/constants/routes";
import SubscriptionRenewalModal from "./SubscriptionRenewalModal";
import { useTrialStatus } from "@/app/_module/hooks/useTrialStatus";
import { usePhoneNumberManager } from "@/app/_module/hooks/usePhoneNumberPricing";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { accountTypeHasTelephony } from "@/app/_module/components/TelephonyEnabled";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";

interface TrialExpirationBannerProps {
  onDismiss?: () => void;
  isDismissed?: boolean;
}

export default function TrialExpirationBanner({
  onDismiss,
  isDismissed = false
}: TrialExpirationBannerProps) {
  const router = useRouter();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const {
    trialEndDate,
    daysRemaining,
    isExpired,
    isViewerMode,
    isLoading,
    error,
    hasActiveSubscription,
    hasPaidSubscription,
    isTrialActive
  } = useTrialStatus();

  // Get phone numbers to check for free trial numbers expiring
  const { phoneNumbers } = usePhoneNumberManager();

  // A plan without a phone (Lite) has no number to warn about: its numbers
  // were deactivated on purpose when the plan changed.
  const { user } = useUserContext();
  const planHasPhone = accountTypeHasTelephony((user as any)?.user?.accountType, (user as any)?.user?.trialAccountTypeUsed);

  // Calculate phone number expiration. We look at TWO things:
  //   1. A free trial phone with a future expiry date (warn ≤7 days)
  //   2. ANY phone the user owns that's already in `disabled` status, OR
  //      whose freeTrialExpiresAt is in the past (warn always — user can't
  //      make calls until they reactivate or buy a new one)
  const freeTrialPhone = phoneNumbers?.find((phone: any) =>
    phone.isFreeTrialNumber || phone.is_free_trial_number
  );

  const phoneExpiresAt = freeTrialPhone?.freeTrialExpiresAt || freeTrialPhone?.free_trial_expires_at;
  const phoneDaysRemaining = phoneExpiresAt
    ? Math.ceil((new Date(phoneExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Detect already-expired or fully-disabled phones so we can warn at login
  // (not just when expiring soon). Without this, a user whose number expired
  // sees nothing at all until they actually click dial — terrible UX.
  const allPhones = phoneNumbers || [];
  const hasDisabledFreeTrial = allPhones.some((p: any) =>
    (p.status === 'disabled' || p.status === 'DISABLED') &&
    (p.isFreeTrialNumber || p.is_free_trial_number)
  );
  const hasDisabledPaid = allPhones.some((p: any) =>
    (p.status === 'disabled' || p.status === 'DISABLED') &&
    !(p.isFreeTrialNumber || p.is_free_trial_number)
  );
  const hasAnyActivePhone = allPhones.some((p: any) =>
    p.status === 'active' || p.status === 'ACTIVE'
  );
  // "Phone already expired" = user can't make calls right now
  const hasExpiredPhone = planHasPhone && !hasAnyActivePhone && (hasDisabledFreeTrial || hasDisabledPaid);

  if (isDismissed || isLoading || error) return null;

  // A security lock reads as "inactive", which this banner took for an unpaid trial: a paying owner was
  // told "Trial Expired — Full Lockdown" (Mark Dusevic, 2026-10-15). ViewerModeBanner explains a lock.
  if ((user as any)?.user?.featureOverrides?.viewerModeReason === "account_locked") return null;

  // A paid subscription silences the TRIAL banners only. The expired-phone
  // banner must still show: a full-fledged agent whose free trial number was
  // switched off (Imperium, 2026-09-03) had no active number, could not dial,
  // and saw nothing anywhere — this early return hid the one banner built for
  // exactly that case.
  if (hasPaidSubscription && !hasExpiredPhone) return null;

  // Show banner if any of:
  //   1. Trial is expired
  //   2. Trial active, no auto-renew, ≤7 days left
  //   3. Free phone expiring within 7 days
  //   4. User's phone(s) ALREADY expired and they have nothing active
  const shouldShowTrialBanner = !hasPaidSubscription && trialEndDate && (isExpired || (isTrialActive && !hasActiveSubscription && daysRemaining <= 7));
  const shouldShowPhoneBanner = planHasPhone && !hasPaidSubscription && phoneDaysRemaining !== null && phoneDaysRemaining > 0 && phoneDaysRemaining <= 7;
  const shouldShowExpiredPhoneBanner = hasExpiredPhone;

  if (!shouldShowTrialBanner && !shouldShowPhoneBanner && !shouldShowExpiredPhoneBanner) return null;

  const handleBannerClick = () => {
    if (isExpired) {
      setShowSubscriptionModal(true);
    } else {
      allowCheckout("subscription") && router.push(routes.settings.path + "?tab=subscription");
    }
  };

  const handleUpgrade = (planId: string) => {
    // Handle upgrade logic here
    // This would typically call an API to upgrade the subscription
    allowCheckout("subscription") && router.push(routes.settings.path + "?tab=subscription");
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss?.();
  };

  // Expired phone(s), no active phone — show red "phone expired" banner so
  // the user can't miss it at login. Suppressed when the trial-expired
  // banner is going to render anyway (trial expired = full lockdown, no
  // need to also show this since they can't dial regardless).
  if (shouldShowExpiredPhoneBanner && !shouldShowTrialBanner) {
    // Same words as the dialer banner and dialog (NoActiveNumberNotice).
    const expiredPhone = allPhones
      .filter((p: any) => p.status === 'disabled' || p.status === 'DISABLED')
      .sort((a: any, b: any) => new Date(b.updatedAt || b.updated_at || 0).getTime() - new Date(a.updatedAt || a.updated_at || 0).getTime())[0];
    const expiredCopy = noNumberCopy(
      hasDisabledPaid ? "subscription_expired" : "trial_expired",
      expiredPhone
        ? {
            phoneNumber: expiredPhone.phoneNumber || expiredPhone.phone_number || "",
            expiredAt: hasDisabledPaid
              ? expiredPhone.updatedAt || expiredPhone.updated_at || null
              : expiredPhone.freeTrialExpiresAt || expiredPhone.free_trial_expires_at || expiredPhone.updatedAt || expiredPhone.updated_at || null,
          }
        : null,
    );
    const expiredTitle = expiredCopy.title;
    const expiredBody = expiredCopy.body;
    const handleExpiredClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // "?tab=phone-numbers" was never a settings tab; Area Codes is.
      allowCheckout("area-code") && router.push(numbersSettingsPath());
    };
    return (
      <div
        className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 cursor-pointer hover:bg-red-100 transition-colors duration-200"
        onClick={handleExpiredClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">{expiredTitle}</h3>
              <p className="text-sm text-red-700 mt-1">{expiredBody}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleExpiredClick}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Phone className="h-4 w-4 mr-2" />
              Get a Number
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isExpired) {
    const handleUpgradeClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Navigate directly to subscription page
      allowCheckout("subscription") && router.push(routes.settings.path + "?tab=subscription&billingTab=my-subscription");
    };

    return (
      <div
        className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 cursor-pointer hover:bg-red-100 transition-colors duration-200"
        onClick={handleUpgradeClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Trial Expired - Account in Full Lockdown Mode
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Your trial ended on {trialEndDate ? new Date(trialEndDate).toLocaleDateString() : 'recently'}. All features are locked. Click to upgrade and restore access.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleUpgradeClick}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Upgrade Now
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 cursor-pointer hover:bg-yellow-100 transition-colors duration-200"
      onClick={handleBannerClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">
              {shouldShowTrialBanner && shouldShowPhoneBanner ? 'Trial & Free Phone Ending Soon' : shouldShowPhoneBanner ? 'Free Phone Number Expiring Soon' : 'Trial Ending Soon'}
            </h3>
            <div className="text-sm text-yellow-700 mt-1 space-y-1">
              {shouldShowTrialBanner && trialEndDate && (
                <p>
                  • Your trial ends in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} on {new Date(trialEndDate).toLocaleDateString()}.
                </p>
              )}
              {shouldShowPhoneBanner && phoneExpiresAt && (
                <p className="flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Free phone number {formatPhoneNumber(freeTrialPhone.phoneNumber || freeTrialPhone.phone_number || '')} expires in {phoneDaysRemaining} day{phoneDaysRemaining !== 1 ? 's' : ''} on {new Date(phoneExpiresAt).toLocaleDateString()}.
                </p>
              )}
              <p className="font-medium">
                Upgrade now to keep your {shouldShowTrialBanner && shouldShowPhoneBanner ? 'account & phone number' : shouldShowPhoneBanner ? 'phone number' : 'trial features'} active!
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              allowCheckout("subscription") && router.push(routes.settings.path + "?tab=subscription");
            }}
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Upgrade Now
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="sm"
            className="text-yellow-600 hover:text-yellow-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
